import { RichTextSubject } from "@/modules/froalaToolbar/shared/RichTextSubject";
import { refreshBlockValues } from "@/shared/utils/variable-block";
import { refreshPillValues } from "@/shared/utils/variable-pill";
import { SingleVariablePopover } from "@components/ui/single-variable-popover";
import { track } from "@/features/analytics";
import {
  VARIABLES_EVENTS,
  trackDateVariableEdit,
  trackInsertVariable,
} from "@features/dynamic-variables/analytics/variables-analytics";
import { DateVariablePopover } from "@features/dynamic-variables/date-variable";
import {
  InlineVariableTypeahead,
  shouldOpenTypeahead,
  useTypeaheadVariables,
} from "@features/dynamic-variables/inline-typeahead";
import type { PickerVariable } from "@features/dynamic-variables/variable-picker/types";
import { openRepeatableRowsForName } from "@features/dynamic-variables/repeatable/open-repeatable-rows-dialog";
import { StyledRichTextWrapper } from "@library-elements/text";
import { useAppDispatch, useAppSelector } from "@store";
import { editEntity } from "@store/story";
import { getEntitySettings } from "@store/story/selectors";
import { baseKeymap } from "prosemirror-commands";
import { keymap } from "prosemirror-keymap";
import { EditorState, Selection } from "prosemirror-state";
import { Transform } from "prosemirror-transform";
import { EditorView } from "prosemirror-view";
import { ResizeAxis } from "@editor-components/ResizeAxis";
import { useEffect, useRef, useState } from "react";
// ProseMirror's own stylesheet: keeps the separator <img> it places after an
// inline atom (emoji, icon, counter) inline — Tailwind's preflight would make
// it block-level and push the caret onto a new line — and styles the
// trailing-break / hidden-selection helpers.
import "prosemirror-view/style/prosemirror.css";
import "../styles/prosemirror.scss";
import { withAnimationDelays } from "./prosemirror/animation-delays";
import { autolinkPlugin } from "./prosemirror/autolink";
import { parseHtml, serializeHtml } from "./prosemirror/html";
import { buildKeymap } from "./prosemirror/keymap";
import { isFileOnlyPaste, pasteProps } from "./prosemirror/paste";
import { derivedAttrsPlugin } from "./prosemirror/plugins/derived-attrs";
import { placeholderPlugin } from "./prosemirror/plugins/placeholder";
import { deriveRichTextData } from "./prosemirror/toolbar-state";
import { handleToolbarEvent } from "./prosemirror/toolbar/handle-toolbar-event";
import {
  type PillContext,
  type PillPopover,
  applyVariableBlocks,
  clickSelectedPill,
  hasTypeaheadTrigger,
  insertVariableToken,
  pillPopoverFor,
  setPillName,
  variableBlockView,
  variablePillView,
  variableTokensPlugin,
} from "./prosemirror/variables";
import type { RichTextProps } from "./Text.editor";
import { MAPPED_PLACEHOLDERS } from "./froala/constants";
import { namespaceInsertedToken } from "./froala/toolbar-handlers/namespace-inserted-token";

/** Matches Froala's saveInterval — commits are debounced typing bursts. */
const COMMIT_DEBOUNCE_MS = 500;

export const ProseMirrorText = ({
  id,
  pills,
}: {
  id: string;
  /** Same data + decorator the inactive render uses, so pills match it. */
  pills: PillContext;
}) => {
  const dispatch = useAppDispatch();
  const pillsRef = useRef(pills);
  pillsRef.current = pills;

  // Stored HTML → editor doc. A version also shows `{{{blocks}}}` as their
  // value; converting here (not in a transaction) keeps opening an element
  // from counting as an edit.
  const toEditorDoc = (html: string) => {
    const doc = parseHtml(html);
    return pillsRef.current.isVersion
      ? applyVariableBlocks(new Transform(doc)).doc
      : doc;
  };

  // Popover for the pill at `pos`. `rect` is captured at the click so the
  // popover never measures a detached node; `pos` re-finds the live one.
  const [pillPopover, setPillPopover] = useState<
    | (Exclude<PillPopover, { kind: "repeatable" }> & {
        pos: number;
        anchor: HTMLElement;
        rect: DOMRect;
      })
    | null
  >(null);

  // Opens what a selected pill edits (see clickSelectedPill); returns whether
  // the click was consumed.
  const openPillPopover = (view: EditorView, pos: number): boolean => {
    const node = view.state.doc.nodeAt(pos);
    const anchor = view.nodeDOM(pos);
    if (node?.type.name !== "variable" || !(anchor instanceof HTMLElement))
      return false;
    const target = pillPopoverFor(node.attrs.name as string, pillsRef.current);
    if (!target) return false;
    if (target.kind === "repeatable") {
      return openRepeatableRowsForName(
        target.name,
        pillsRef.current.repeatableRoots,
      );
    }
    setPillPopover({
      ...target,
      pos,
      anchor,
      rect: anchor.getBoundingClientRect(),
    });
    return true;
  };
  const openPillPopoverRef = useRef(openPillPopover);
  openPillPopoverRef.current = openPillPopover;

  // ======= inline "{{" typeahead (Froala parity) ========
  // Typing a second "{" opens the variable search at the caret; the popover's
  // own search input takes focus, so the editor only ever holds the literal
  // "{{" — replaced by the pick, removed on cancel. Authoring only: a version
  // fills a template's variables in and never defines new ones (the manifest
  // the share dialog and publish read comes from the master story).
  // `typeaheadOpenRef` flips synchronously so keys typed before the search
  // input has focus are swallowed rather than landing in the editor.
  const typeaheadOpenRef = useRef(false);
  const [typeahead, setTypeahead] = useState<{
    pos: number;
    rect: DOMRect;
  } | null>(null);
  const { variables: typeaheadVariables, isLoading: isTypeaheadLoading } =
    useTypeaheadVariables();

  const openTypeahead = (view: EditorView, from: number, text: string) => {
    const before = view.state.doc.textBetween(Math.max(0, from - 1), from);
    if (pillsRef.current.isVersion || !shouldOpenTypeahead(text, before))
      return;
    typeaheadOpenRef.current = true;
    track(VARIABLES_EVENTS.OPEN_VARIABLE_TYPEAHEAD);
    // Let the second "{" land first, then anchor at the caret after it.
    requestAnimationFrame(() => {
      const live = viewRef.current;
      if (!live || !typeaheadOpenRef.current) return;
      const pos = live.state.selection.from;
      const { left, top, bottom } = live.coordsAtPos(pos);
      setTypeahead({ pos, rect: new DOMRect(left, top, 0, bottom - top) });
      (live.dom as HTMLElement).blur();
    });
  };
  const openTypeaheadRef = useRef(openTypeahead);
  openTypeaheadRef.current = openTypeahead;

  const closeTypeahead = () => {
    typeaheadOpenRef.current = false;
    setTypeahead(null);
  };

  const selectTypeaheadVariable = (variable: PickerVariable) => {
    const view = viewRef.current;
    const target = typeahead;
    closeTypeahead();
    if (!view || !target) return;
    trackInsertVariable({
      variable,
      method: "typeahead",
      targetElement: "RichText",
    });
    // Inside a repeatable group the token is namespaced + registered as a
    // field — the typeahead inserts directly, so it applies that here.
    const token = namespaceInsertedToken(id, variable.token);
    const range = hasTypeaheadTrigger(view.state.doc, target.pos)
      ? { from: target.pos - 2, to: target.pos }
      : undefined;
    insertVariableToken(token, range)(view.state, view.dispatch);
    view.focus();
  };

  // Escape / Backspace on an empty search: the "{{" goes with it (left behind
  // it's a half-typed token the variable manifest would misread) and typing
  // resumes where it was.
  const cancelTypeahead = () => {
    const view = viewRef.current;
    const target = typeahead;
    closeTypeahead();
    if (!view || !target) return;
    if (hasTypeaheadTrigger(view.state.doc, target.pos)) {
      view.dispatch(view.state.tr.delete(target.pos - 2, target.pos));
    }
    view.focus();
  };
  const settings = useAppSelector((state) =>
    getEntitySettings<RichTextProps>(id)(state),
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<EditorView | null>(null);
  // HTML as of the editor's last local state — lets external pushes skip
  // redundant doc replacement (e.g. our own echo coming back from the store).
  const lastHtmlRef = useRef<string>("");
  // HTML the store last received from (or pushed into) this editor. Its echo
  // must not replace the doc — the user may have typed on since the commit.
  const committedHtmlRef = useRef<string>("");
  const contentRef = useRef(settings.content);
  contentRef.current = settings.content;
  // Read at commit time: the stored text carries Froala's per-line reveal
  // delays for the element's animation (see withAnimationDelays).
  const animationRef = useRef(settings.animation);
  animationRef.current = settings.animation;

  // Debounced store commit. Per-keystroke dispatch would create one Redux
  // history frame per character (evicting the 100-frame story history) and
  // one collab broadcast per keystroke, so edits are committed in typing
  // bursts like Froala's save plugin did.
  const commitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingHtmlRef = useRef<string | null>(null);

  const cancelPendingCommit = () => {
    if (commitTimerRef.current) {
      clearTimeout(commitTimerRef.current);
      commitTimerRef.current = null;
    }
    pendingHtmlRef.current = null;
  };

  const flushPendingCommit = () => {
    if (commitTimerRef.current) {
      clearTimeout(commitTimerRef.current);
      commitTimerRef.current = null;
    }
    if (pendingHtmlRef.current == null) return;
    const html = withAnimationDelays(
      pendingHtmlRef.current,
      animationRef.current,
    );
    pendingHtmlRef.current = null;
    committedHtmlRef.current = html;
    dispatch(editEntity({ data: { content: html }, id }));
  };
  const flushPendingCommitRef = useRef(flushPendingCommit);
  flushPendingCommitRef.current = flushPendingCommit;

  const queueCommit = (html: string) => {
    pendingHtmlRef.current = html;
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    commitTimerRef.current = setTimeout(
      () => flushPendingCommitRef.current(),
      COMMIT_DEBOUNCE_MS,
    );
  };

  // The shared FroalaToolbar is driven by "refresh" events carrying the
  // editor's state; emit one after every applied transaction (typing,
  // selection moves, commands) so its controls stay in sync.
  const emitRefresh = () => {
    const view = viewRef.current;
    if (!view) return;
    RichTextSubject.emit({
      type: "refresh",
      editorId: id,
      data: deriveRichTextData(view),
    });
  };

  // Replace the whole doc from an HTML string. commit=false for external
  // pushes (store already has this content); commit=true when the change
  // originates here (AI generate) and must reach the store. Either way a
  // pending typed-burst commit is stale now — drop it.
  const replaceDoc = (html: string, { commit }: { commit: boolean }) => {
    const view = viewRef.current;
    if (!view) return;
    cancelPendingCommit();
    const doc = toEditorDoc(html);
    view.updateState(
      EditorState.create({
        doc,
        plugins: view.state.plugins,
        // Caret at the end after a replace — matches Froala's undo/redo
        // selection restore.
        selection: Selection.atEnd(doc),
      }),
    );
    lastHtmlRef.current = html;
    committedHtmlRef.current = html;
    if (commit) {
      const serialized = serializeHtml(doc);
      const stored = withAnimationDelays(serialized, animationRef.current);
      lastHtmlRef.current = serialized;
      committedHtmlRef.current = stored;
      dispatch(editEntity({ data: { content: stored }, id }));
    }
    emitRefresh();
  };
  const replaceDocRef = useRef(replaceDoc);
  replaceDocRef.current = replaceDoc;

  // Mount once per activation (Text.editor.tsx remounts via key=dragKey).
  // Content changes while mounted arrive via __CONTENT_TRIGGER_UPDATE__ or
  // the store-sync effect below — never via re-mount.
  useEffect(() => {
    if (!containerRef.current) return;

    const initialHtml = settings.content ?? "";
    // An empty element starts in its placeholder's block, as Froala does: a
    // `<h2>` placeholder gives a Title line, so the placeholder shows at title
    // size and typing produces a Title (QA B3 — the blank slide's title). Only
    // the editor starts there; the store keeps "" until something is typed.
    const placeholderTag = settings.placeholder?.slice(0, 4) ?? "";
    const startHtml =
      initialHtml ||
      MAPPED_PLACEHOLDERS[placeholderTag as keyof typeof MAPPED_PLACEHOLDERS] ||
      "";
    const view = new EditorView(containerRef.current, {
      state: EditorState.create({
        doc: toEditorDoc(startHtml),
        plugins: [
          buildKeymap(),
          keymap(baseKeymap),
          autolinkPlugin(),
          variableTokensPlugin({ blocks: pills.isVersion }),
          derivedAttrsPlugin(),
          placeholderPlugin(settings.placeholder ?? null),
        ],
      }),
      attributes: {
        class: "pm-editor",
        id: `rich-text-${id}`,
      },
      nodeViews: {
        variable: variablePillView(() => pillsRef.current),
        variable_block: variableBlockView(() => pillsRef.current),
      },
      handleTextInput(view, from, _to, text) {
        if (typeaheadOpenRef.current) return true;
        openTypeaheadRef.current(view, from, text);
        return false;
      },
      handleClickOn: clickSelectedPill((view, pos) =>
        openPillPopoverRef.current(view, pos),
      ),
      // Paste pipeline (spec §2) — ProseMirror's own clipboard hooks.
      ...pasteProps,
      // Undo/redo is story-level (Redux or Yjs, via use-shortcuts / the header
      // buttons). The typing burst still waiting on the commit debounce has to
      // land first, or undo reverts the step before it and the burst is lost.
      handleKeyDown(_view, event) {
        // The typeahead's search input owns the keyboard while it is open.
        if (typeaheadOpenRef.current) return true;
        const key = event.key.toLowerCase();
        if ((event.metaKey || event.ctrlKey) && (key === "z" || key === "y")) {
          flushPendingCommitRef.current();
        }
        return false;
      },
      handleDOMEvents: {
        blur: () => {
          flushPendingCommitRef.current();
          return false;
        },
        // `true` skips ProseMirror's paste without preventDefault, so the
        // shell's paste listener still creates the Image element.
        paste: (_view, event) => isFileOnlyPaste(event),
      },
      dispatchTransaction(tr) {
        const current = viewRef.current;
        if (!current) return;
        const nextState = current.state.apply(tr);
        current.updateState(nextState);
        if (tr.docChanged) {
          const html = serializeHtml(nextState.doc);
          lastHtmlRef.current = html;
          queueCommit(html);
        }
        emitRefresh();
      },
    });
    viewRef.current = view;
    lastHtmlRef.current = initialHtml;
    committedHtmlRef.current = initialHtml;
    emitRefresh();

    // Activation focus: caret at the end, matching Froala's behavior.
    view.dispatch(view.state.tr.setSelection(Selection.atEnd(view.state.doc)));
    view.focus();

    return () => {
      // Deactivation: the tail of the last typing burst must reach the
      // store before the read-only render takes over.
      flushPendingCommitRef.current();
      // Same teardown signal Froala sends; the toolbar unmounts its controls.
      RichTextSubject.emit({
        type: "onFroalaDestroy",
        editorId: id,
        data: true,
      });
      viewRef.current = null;
      view.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-once by design
  }, [id]);

  // Reload, closing the tab or switching away within the commit debounce lost
  // the last typing burst — it never reached the store (QA J7).
  useEffect(() => {
    const flush = () => flushPendingCommitRef.current();
    const flushWhenHidden = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", flushWhenHidden);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", flushWhenHidden);
    };
  }, []);

  // External push contract used by the AI agent and Redux undo/redo paths.
  useEffect(() => {
    window.__CONTENT_TRIGGER_UPDATE__ = (payloadId, content) => {
      if (payloadId !== id) return;
      replaceDocRef.current(content ?? contentRef.current ?? "", {
        commit: false,
      });
    };
    return () => {
      window.__CONTENT_TRIGGER_UPDATE__ = undefined;
    };
  }, [id]);

  // The live pill for the open popover — its DOM node is replaced when the
  // pill's attributes change, so re-find it by position.
  const resolvePillAnchor = () => {
    const view = viewRef.current;
    if (!view || !pillPopover) return null;
    const dom = view.nodeDOM(pillPopover.pos);
    return dom instanceof HTMLElement ? dom : null;
  };

  // Keep the pill's badge visible while its popover is open (pill CSS).
  useEffect(() => {
    const anchor = pillPopover?.anchor;
    if (!anchor) return;
    anchor.setAttribute("data-active", "true");
    return () => anchor.removeAttribute("data-active");
  }, [pillPopover?.anchor]);

  // A new format / offset renames the pill: it saves, syncs and undoes like
  // any other edit.
  const handleDateTokenSave = (nextToken: string) => {
    const view = viewRef.current;
    const target = pillPopover;
    setPillPopover(null);
    if (!view || target?.kind !== "date") return;
    if (setPillName(target.pos, nextToken)(view.state, view.dispatch)) {
      trackDateVariableEdit(nextToken);
    }
    view.focus();
  };

  // Version data or calc formulas changed → relabel the live pills in place
  // (Froala runs the same refresh on its editable).
  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    refreshPillValues(view.dom, pills.data, pills.decorate);
    refreshBlockValues(view.dom, pills.data);
  }, [pills.data, pills.decorate]);

  // Turning the animation on or off, or changing its speed, rewrites the
  // stored per-line delays, as Froala does on the same change. Not on mount:
  // text without delays gets them with its next edit.
  const animationKey = settings.animation
    ? `on:${settings.animation.speed ?? ""}`
    : "off";
  const appliedAnimationKeyRef = useRef(animationKey);
  useEffect(() => {
    if (appliedAnimationKeyRef.current === animationKey) return;
    appliedAnimationKeyRef.current = animationKey;
    const current = contentRef.current ?? "";
    const next = withAnimationDelays(current, animationRef.current);
    if (next === current) return;
    committedHtmlRef.current = next;
    dispatch(editEntity({ data: { content: next }, id }));
  }, [animationKey, dispatch, id]);

  // Store content changed by something other than this editor while it is
  // open — undo/redo above all. Redux undo flags isInRedoUndo, but the Yjs
  // UndoManager (collab rooms) reaches the store through the remote-apply path
  // with no flag, so any content that isn't our own echo is pushed in.
  useEffect(() => {
    const html = settings.content ?? "";
    if (html === lastHtmlRef.current || html === committedHtmlRef.current)
      return;
    replaceDocRef.current(html, { commit: false });
  }, [settings.content]);

  // Toolbar protocol: the shared FroalaToolbar emits its control events over
  // RichTextSubject; AI generation replaces the doc and commits (a user edit).
  useEffect(
    () =>
      RichTextSubject.on((event) => {
        if (event.editorId !== id) return;
        if (event.type === "refresh" || event.type === "onFroalaDestroy")
          return;
        if (event.type === "ai") {
          replaceDocRef.current(event.data.content, { commit: true });
          return;
        }
        const view = viewRef.current;
        if (!view) return;
        handleToolbarEvent(view, event);
        view.focus();
      }),
    [id],
  );

  // ======= resize (same chrome Froala mounted: purple outline + width handles) ========
  const [width, setWidth] = useState<number>(settings.width || 100);
  const [elementDomWidth, setElementDomWidth] = useState<number>(0);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  useEffect(() => {
    setWidth(settings.width || 100);
  }, [settings.width]);

  useEffect(() => {
    const measure = () => {
      const container = containerRef.current;
      if (!container) return;
      setContainerWidth(container.clientWidth);
      const element = container.closest<HTMLElement>(
        '[data-element-name="RichText"]',
      );
      if (element) setElementDomWidth(element.clientWidth);
    };
    measure();
    const timeout = setTimeout(measure, 250);
    return () => clearTimeout(timeout);
  }, [width]);
  // ======= resize end ========

  // Active-editing visual parity with the read-only render: honor the
  // element's width (%) and horizontal alignment so line wrapping doesn't
  // jump when the editor mounts.
  return (
    <StyledRichTextWrapper $horizontalAlignment={settings.horizontalAlignment}>
      <div
        ref={containerRef}
        // Same classes Froala's editor box carried: container styles are
        // written against them (a table cell's alignment, the in-cell layout),
        // so without them the open editor lost the alignment the read-only
        // render had and collapsed to its content width inside a cell.
        className="pm-editor-wrapper content rich-text-element"
        style={{
          width: `${width || 100}%`,
          position: "relative",
        }}
      />
      {!settings.disableResize && (
        <ResizeAxis
          widthProps={{
            width: String(width || 100),
            property: "width",
            min: 10,
            max: 100,
            elementDomWidth,
            containerWidth,
            emitWidth: (value: string) => setWidth(Number(value)),
          }}
          heightProps={{
            height: 0,
            property: "",
            containerHeight: 0,
            emitHeight: () => {},
          }}
          containerHeight={containerRef.current?.offsetHeight}
        />
      )}
      {pillPopover?.kind === "value" && (
        <SingleVariablePopover
          multiline
          anchorEl={pillPopover.anchor}
          resolveAnchorEl={resolvePillAnchor}
          anchorRect={pillPopover.rect}
          variableName={pillPopover.name}
          valueType={pillPopover.valueType}
          onClose={() => setPillPopover(null)}
        />
      )}
      {typeahead && (
        <InlineVariableTypeahead
          variables={typeaheadVariables}
          isLoading={isTypeaheadLoading}
          anchorRect={typeahead.rect}
          onSelect={selectTypeaheadVariable}
          onClose={closeTypeahead}
          onCancel={cancelTypeahead}
        />
      )}
      {pillPopover?.kind === "date" && (
        <DateVariablePopover
          anchorEl={pillPopover.anchor}
          resolveAnchorEl={resolvePillAnchor}
          anchorRect={pillPopover.rect}
          token={pillPopover.token}
          onClose={() => setPillPopover(null)}
          onSave={handleDateTokenSave}
        />
      )}
    </StyledRichTextWrapper>
  );
};
